import type { ReactNode } from 'react';
import InlineStatus from './InlineStatus';
import type { AutomationCoverageSummary } from '../../lib/automationCoverage';

interface AutomationCoverageRailProps {
  summary: AutomationCoverageSummary;
  action?: ReactNode;
  className?: string;
  testId?: string;
}

export default function AutomationCoverageRail({
  summary,
  action,
  className = '',
  testId,
}: AutomationCoverageRailProps) {
  if (summary.state === 'loading') {
    return (
      <div
        className={`rounded-xl border border-border bg-surface p-5 ${className}`.trim()}
        data-testid={testId}
      >
        <div className="space-y-3">
          <div className="skeleton h-4 w-40" />
          <div className="grid gap-2 sm:grid-cols-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="rounded-lg border border-border bg-surface-active p-3">
                <div className="skeleton h-3 w-20" />
                <div className="skeleton mt-2 h-4 w-full" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={testId}>
      <InlineStatus
        tone={summary.tone}
        title={summary.title}
        message={(
          <div className="space-y-3">
            <div className="grid gap-2 sm:grid-cols-3">
              {summary.facts.map((fact) => (
                <div key={fact.key} className="rounded-lg border border-border bg-surface-active/70 p-3">
                  <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-gray-500">
                    {fact.label}
                  </p>
                  <p
                    className={[
                      'mt-2 text-sm text-gray-100',
                      fact.mono ? 'font-mono tabular-nums' : '',
                      fact.tone === 'success'
                        ? 'text-emerald-300'
                        : fact.tone === 'warning'
                          ? 'text-amber-300'
                          : '',
                    ].filter(Boolean).join(' ')}
                  >
                    {fact.value}
                  </p>
                </div>
              ))}
            </div>
            <p className="text-sm text-gray-300">{summary.message}</p>
          </div>
        )}
        action={action}
        className={`rounded-xl ${className}`.trim()}
      />
    </div>
  );
}
